import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import DesignModule from "./DesignModule";
import { api } from "./api";
import "./Drawings3D.css";

const Drawings3D = ({ project }) => {
  const navigate = useNavigate();

  const projectId = project?._id || project?.id;

  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const projectName =
    project?.name ||
    project?.projectName ||
    project?.leadName ||
    "Project Management Dashboard";

  const getCurrentUser = () => {
    try {
      const userData = localStorage.getItem("user");
      const parsedUser = userData ? JSON.parse(userData) : null;

      return {
        userId: parsedUser?._id || parsedUser?.id || null,
        userName:
          parsedUser?.name ||
          parsedUser?.userName ||
          localStorage.getItem("userName") ||
          localStorage.getItem("name") ||
          "Unknown User",
      };
    } catch (error) {
      return {
        userId: null,
        userName:
          localStorage.getItem("userName") ||
          localStorage.getItem("name") ||
          "Unknown User",
      };
    }
  };

  const loadComments = async () => {
    if (!projectId) return;

    try {
      setLoadingComments(true);
      const res = await api.get(`/drawing-design/${projectId}/comments`);
      setComments(res.data || []);
    } catch (error) {
      console.error("Load comments error:", error);
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleAddComment = async () => {
    if (!comment.trim()) return;

    if (!projectId) {
      alert("Project ID missing");
      return;
    }

    try {
      setSavingComment(true);

      const currentUser = getCurrentUser();

      const res = await api.post(`/drawing-design/${projectId}/comments`, {
        comment: comment.trim(),
        userId: currentUser.userId,
        userName: currentUser.userName,
      });

      setComments((prev) => [res.data, ...prev]);
      setComment("");
    } catch (error) {
      console.error("Comment save error:", error);
      alert("Failed to save comment");
    } finally {
      setSavingComment(false);
    }
  };

  return (
    <div className="d3-page" style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div className="d3-header-inner" style={styles.headerInner}>
          <button
            className="d3-back"
            onClick={() => navigate(-1)}
            style={styles.backBtn}
          >
            ← Back
          </button>

          <div className="d3-header-title" style={{ textAlign: "center" }}>
            <h1 className="d3-heading" style={styles.heading}>
              Drawings & 3D
            </h1>
            <p className="d3-subheading" style={styles.subHeading}>
              Design Management Dashboard
            </p>
          </div>

          <div className="d3-spacer" style={{ width: "95px" }} />
        </div>
      </div>

      {/* Project Card */}
      <div className="d3-container" style={styles.container}>
        <div className="d3-project-card" style={styles.projectCard}>
          <div>
            <p style={styles.label}>Current Project</p>
            <h2 className="d3-project-name" style={styles.projectName}>
              {projectName}
            </h2>
          </div>

          <div className="d3-badge" style={styles.badge}>
            DESIGN MODULE
          </div>
        </div>
      </div>

      {/* Design Module */}
      <div className="d3-container" style={styles.container}>
        <div className="d3-module-card" style={styles.moduleCard}>
          <div style={styles.moduleHeader}></div>

          <div className="d3-module-body" style={{ padding: "24px" }}>
            <DesignModule project={project} />
          </div>
        </div>
      </div>

      {/* Comment Section */}
      <div className="d3-container" style={styles.container}>
        <div className="d3-comment-card" style={styles.commentCard}>
          <div style={styles.commentHeader}>
            <h3 style={styles.commentTitle}>Comments</h3>
            <span style={styles.commentCount}>{comments.length}</span>
          </div>

          <div className="d3-input-box" style={styles.inputBox}>
            <textarea
              className="d3-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your comment here..."
              style={styles.textarea}
            />

            <button
              className="d3-comment-btn"
              onClick={handleAddComment}
              disabled={savingComment}
              style={{
                ...styles.commentBtn,
                opacity: savingComment ? 0.6 : 1,
                cursor: savingComment ? "not-allowed" : "pointer",
              }}
            >
              {savingComment ? "Saving..." : "Add Comment"}
            </button>
          </div>

          <div style={styles.commentList}>
            {loadingComments ? (
              <div style={styles.emptyComment}>Loading comments...</div>
            ) : comments.length === 0 ? (
              <div style={styles.emptyComment}>
                No comments yet. Add first comment.
              </div>
            ) : (
              comments.map((item) => (
                <div
                  key={item._id || item.id}
                  className="d3-comment-item"
                  style={styles.commentItem}
                >
                  <div className="d3-avatar" style={styles.avatar}>
                    {item.userName?.charAt(0)?.toUpperCase() || "U"}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="d3-comment-top" style={styles.commentTop}>
                      <strong>{item.userName || "Unknown User"}</strong>
                      <span>
                        {item.createdAt
                          ? new Date(item.createdAt).toLocaleString()
                          : "-"}
                      </span>
                    </div>

                    <p className="d3-comment-text" style={styles.commentText}>
                      {item.comment || item.text}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f0fdfa 0%, #ffffff 100%)",
    fontFamily: "'Inter', 'Segoe UI', Roboto, Arial, sans-serif",
  },

  header: {
    background: "#ffffff",
    borderBottom: "1px solid #99f6e4",
    position: "sticky",
    top: 0,
    zIndex: 100,
    boxShadow: "0 8px 24px rgba(15,118,110,0.08)",
  },

  headerInner: {
    maxWidth: "1500px",
    margin: "0 auto",
    padding: "18px 24px",
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: "18px",
  },

  backBtn: {
    padding: "11px 18px",
    borderRadius: "12px",
    border: "1px solid #0f766e",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "700",
    color: "#0f766e",
  },

  heading: {
    margin: 0,
    fontSize: "34px",
    fontWeight: "900",
    color: "#134e4a",
  },

  subHeading: {
    margin: "6px 0 0",
    color: "#0f766e",
    fontSize: "15px",
    fontWeight: "600",
  },

  container: {
    maxWidth: "1500px",
    margin: "24px auto 0",
    padding: "0 24px",
  },

  projectCard: {
    background: "#ffffff",
    borderRadius: "20px",
    padding: "22px 26px",
    border: "1px solid #99f6e4",
    boxShadow: "0 12px 30px rgba(15,118,110,0.10)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "18px",
  },

  label: {
    margin: "0 0 6px",
    color: "#0f766e",
    fontSize: "13px",
    fontWeight: "800",
    textTransform: "uppercase",
  },

  projectName: {
    margin: 0,
    color: "#134e4a",
    fontSize: "26px",
    fontWeight: "900",
  },

  badge: {
    padding: "10px 18px",
    borderRadius: "999px",
    background: "#ccfbf1",
    color: "#115e59",
    fontSize: "14px",
    fontWeight: "800",
    border: "1px solid #5eead4",
  },

  moduleCard: {
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid #99f6e4",
    boxShadow: "0 14px 34px rgba(15,118,110,0.10)",
    overflow: "hidden",
    minHeight: "600px",
  },

  moduleHeader: {
    padding: "18px 24px",
    borderBottom: "1px solid #99f6e4",
    background: "linear-gradient(90deg, #f0fdfa, #ffffff)",
  },

  moduleTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "900",
    color: "#134e4a",
  },

  moduleSubTitle: {
    margin: "6px 0 0",
    color: "#0f766e",
    fontSize: "14px",
    fontWeight: "600",
  },

  commentCard: {
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid #99f6e4",
    boxShadow: "0 14px 34px rgba(15,118,110,0.10)",
    padding: "24px",
    marginBottom: "34px",
  },

  commentHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "18px",
  },

  commentTitle: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "900",
    color: "#134e4a",
  },

  commentCount: {
    background: "#ccfbf1",
    color: "#0f766e",
    padding: "4px 10px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: "800",
  },

  inputBox: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
    marginBottom: "22px",
  },

  textarea: {
    flex: 1,
    minHeight: "90px",
    resize: "vertical",
    border: "1px solid #99f6e4",
    borderRadius: "14px",
    padding: "14px",
    fontSize: "15px",
    outline: "none",
  },

  commentBtn: {
    padding: "13px 20px",
    borderRadius: "12px",
    border: "none",
    background: "#0f766e",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "800",
  },

  commentList: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },

  emptyComment: {
    padding: "20px",
    borderRadius: "14px",
    background: "#f0fdfa",
    color: "#0f766e",
    fontWeight: "600",
    textAlign: "center",
  },

  commentItem: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    background: "#f0fdfa",
    border: "1px solid #99f6e4",
  },

  avatar: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    background: "#0f766e",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "900",
    flexShrink: 0,
  },

  commentTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    color: "#115e59",
    fontSize: "14px",
    flexWrap: "wrap",
  },

  commentText: {
    margin: "8px 0 0",
    color: "#134e4a",
    fontSize: "15px",
    lineHeight: "1.5",
  },
};

export default Drawings3D;
